
import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { HasPermissionDirective } from '@olv/ui-shared/directives/has-permission.directive';
import { Resources, Scopes } from '@olv/ui-shared/models/settlement-permissions.model';
import { DatePickerService, RangeOfDate } from '@olv/ui-shared/services/date-picker.service';
import { ReportsService } from '@olv/ui-shared/services/reports.service';
import { DatePickerComponent } from '@olv/ui-shared/ui-shared/date-picker/date-picker.component';
import { DropdownComponent } from '@olv/ui-shared/ui-shared/dropdown/dropdown.component';
import { ButtonComponent } from '@olv/ui-shared/ui-shared/button/button.component';

@Component({
  selector: 'app-reports-assets',
  standalone: true,
  imports: [FormsModule, HasPermissionDirective, DatePickerComponent, DropdownComponent, ButtonComponent],
  templateUrl: './reports-assets.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './reports-assets.component.scss',
})
export class ReportsAssetsComponent implements OnInit {
  protected readonly Resources = Resources;
  protected readonly Scopes = Scopes;
  private readonly reportsService = inject(ReportsService);
  private readonly datePickerService = inject(DatePickerService);

  selectedDateRange: RangeOfDate | null = null;
  dateFilter = {
    key: 'dateAssets',
    label: 'Date',
    start: null as Date | null,
    end: null as Date | null,
  };
  selectedMerchantId = '';
  merchantOptions: { value: string; label: string }[] = [];
  merchantsLoading = false;
  isLoading = false;

  ngOnInit(): void {
    this.loadAssetsMerchants();
  }

  onGenerate(event: Event): void {
    event.preventDefault();

    if (
      this.isLoading ||
      this.isGenerateDisabled ||
      !this.selectedDateRange?.start ||
      !this.selectedDateRange?.end ||
      !this.selectedMerchantId
    ) {
      return;
    }

    this.isLoading = true;

    this.reportsService
      .exportAssets({
        merchantId: this.selectedMerchantId,
        startDate: this.toYmd(this.selectedDateRange.start),
        endDate: this.toYmd(this.selectedDateRange.end),
      })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (result) => {
          if (!result) {
            return;
          }

          this.downloadBlob(result.blob, result.filename);
        },
        error: (err) => {
          console.error('Error exporting assets report:', err);
        },
      });
  }

  onDateRangeSelected(range: RangeOfDate): void {
    const selectedRange = this.datePickerService.dateRangeSelected(range);

    this.selectedDateRange = selectedRange;
    this.dateFilter = { ...this.dateFilter, start: selectedRange.start, end: selectedRange.end };
  }

  onMerchantSelected(merchantId: string): void {
    this.selectedMerchantId = merchantId;
  }

  private loadAssetsMerchants(): void {
    this.merchantsLoading = true;

    this.reportsService
      .getAssetsMerchants()
      .pipe(finalize(() => (this.merchantsLoading = false)))
      .subscribe({
        next: (merchants) => {
          this.merchantOptions = merchants.map((merchant) => ({
            value: merchant.id,
            label: merchant.name,
          }));
        },
        error: (err) => {
          console.error('Error loading assets merchants:', err);
        },
      });
  }

  private toYmd(date: Date): string {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    return d.toISOString().slice(0, 10);
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  get isGenerateDisabled(): boolean {
    return (
      !this.selectedDateRange?.start ||
      !this.selectedDateRange?.end ||
      !this.selectedMerchantId
    );
  }

  get generateButtonState(): 'disabled' | 'default' {
    return this.isGenerateDisabled || this.isLoading ? 'disabled' : 'default';
  }
}
